/**
 * CR283: Merchant Branding Card Component
 * Phase Y: Added logo upload + remove functionality
 */

import { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Save, Loader2, Store, X, Upload, Trash2 } from "lucide-react";
import type { MerchantDirectoryRow } from "@/hooks/useMerchantsDirectory";
import { useMerchantDirectoryMutations } from "@/hooks/useMerchantsDirectory";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface MerchantBrandingCardProps {
  merchant: MerchantDirectoryRow;
}

const ACCEPTED_FORMATS = ".png,.jpg,.jpeg,.svg";
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function MerchantBrandingCard({ merchant }: MerchantBrandingCardProps) {
  const [logoUrl, setLogoUrl] = useState(merchant.logo_url ?? "");
  const [logoSvg, setLogoSvg] = useState(merchant.logo_svg ?? "");
  const [logoSource, setLogoSource] = useState(merchant.logo_source ?? "");
  const [logoAttribution, setLogoAttribution] = useState(merchant.logo_attribution ?? "");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { toast } = useToast();
  const { updateDetails, isUpdatingDetails } = useMerchantDirectoryMutations();

  const hasChanges =
    logoUrl !== (merchant.logo_url ?? "") ||
    logoSvg !== (merchant.logo_svg ?? "") ||
    logoSource !== (merchant.logo_source ?? "") ||
    logoAttribution !== (merchant.logo_attribution ?? "");

  const handleSave = async () => {
    await updateDetails({
      merchantKey: merchant.merchant_key,
      branding: {
        logo_url: logoUrl || null,
        logo_svg: logoSvg || null,
        logo_source: logoSource || null,
        logo_attribution: logoAttribution || null,
      },
    });
  };

  const handleClearLogo = () => {
    setLogoUrl("");
    setLogoSvg("");
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "File too large",
        description: "Maximum file size is 5MB",
        variant: "destructive",
      });
      return;
    }

    // Validate file type
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PNG, JPG, or SVG file",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    try {
      // Generate upload path: merchant-logos/{merchant_key}/{timestamp}-{filename}
      const timestamp = Date.now();
      const sanitizedFilename = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `${merchant.merchant_key}/${timestamp}-${sanitizedFilename}`;

      setUploadProgress(30);

      // Upload to Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from("merchant-logos")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      setUploadProgress(70);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("merchant-logos")
        .getPublicUrl(data.path);

      const publicUrl = urlData.publicUrl;

      setUploadProgress(90);

      // Save via RPC
      await updateDetails({
        merchantKey: merchant.merchant_key,
        branding: {
          logo_url: publicUrl,
          logo_svg: null,
          logo_source: "uploaded",
          logo_attribution: logoAttribution || null,
        },
      });

      // Update local state
      setLogoUrl(publicUrl);
      setLogoSvg("");
      setLogoSource("uploaded");
      setUploadProgress(100);

      toast({
        title: "Logo uploaded",
        description: "Merchant logo has been uploaded successfully",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Upload failed";
      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await updateDetails({
        merchantKey: merchant.merchant_key,
        branding: {
          logo_url: null,
          logo_svg: null,
          logo_source: null,
          logo_attribution: null,
        },
      });

      // Update local state
      setLogoUrl("");
      setLogoSvg("");
      setLogoSource("");
      setLogoAttribution("");

      toast({
        title: "Logo removed",
        description: "Merchant logo has been removed",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to remove logo";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const hasLogo = Boolean(logoUrl || logoSvg);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Branding</CardTitle>
        <CardDescription>
          Logo and attribution information.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Logo Preview */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden border">
            {logoSvg ? (
              <div
                className="w-12 h-12"
                dangerouslySetInnerHTML={{ __html: logoSvg }}
              />
            ) : logoUrl ? (
              <img
                src={logoUrl}
                alt={merchant.display_name}
                className="w-12 h-12 object-contain"
              />
            ) : (
              <Store className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Current Logo</div>
            <div className="text-xs text-muted-foreground">
              {logoUrl ? "URL" : logoSvg ? "SVG" : "No logo set"}
            </div>
          </div>
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}

        {/* Upload / Remove Actions */}
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FORMATS}
            onChange={handleFileSelect}
            className="hidden"
            disabled={isUploading || isUpdatingDetails}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isUpdatingDetails}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Logo
          </Button>
          {hasLogo && (
            <Button
              variant="outline"
              onClick={handleRemoveLogo}
              disabled={isUploading || isUpdatingDetails}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove Logo
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <Label>Logo URL</Label>
          <Input
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://..."
            disabled={isUploading}
          />
        </div>

        <div className="space-y-2">
          <Label>Logo SVG</Label>
          <Textarea
            value={logoSvg}
            onChange={(e) => setLogoSvg(e.target.value)}
            placeholder="<svg>...</svg>"
            className="font-mono text-xs h-24"
            disabled={isUploading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Source</Label>
            <Input
              value={logoSource}
              onChange={(e) => setLogoSource(e.target.value)}
              placeholder="e.g., brand site"
              disabled={isUploading}
            />
          </div>
          <div className="space-y-2">
            <Label>Attribution</Label>
            <Input
              value={logoAttribution}
              onChange={(e) => setLogoAttribution(e.target.value)}
              placeholder="e.g., © Brand Name"
              disabled={isUploading}
            />
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={!hasChanges || isUpdatingDetails || isUploading}
          className="w-full"
        >
          {isUpdatingDetails ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Branding
        </Button>
      </CardContent>
    </Card>
  );
}